import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { CheckCircle2, X } from "lucide-react";
import { getParagraph } from "../content/passage";

interface EvidenceSheetProps {
  paragraphId: string | null;
  title?: string;
  onClose: () => void;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])"
].join(",");

function isAvailableFocusTarget(element: Element | null): element is HTMLElement {
  if (!(element instanceof HTMLElement) || !element.isConnected) return false;
  if (element.matches(":disabled, [aria-disabled='true']")) return false;
  const style = window.getComputedStyle(element);
  return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
}

function focusWithoutScrolling(element: HTMLElement): void {
  element.focus({ preventScroll: true });
}

// 证据页以二维纸张覆盖定格场景，明确区分课文事实与 AI 判断。
export function EvidenceSheet({ paragraphId, title = "课文证据已找到", onClose }: EvidenceSheetProps) {
  const paragraph = paragraphId ? getParagraph(paragraphId) : undefined;
  const open = Boolean(paragraph);
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const lastExternalFocusRef = useRef<HTMLElement | null>(null);
  const previouslyOpenRef = useRef(false);
  const openRef = useRef(open);
  const closeRequestedRef = useRef(false);
  const restoreFrameRef = useRef<number | null>(null);
  openRef.current = open;

  const restoreFocus = useCallback(() => {
    if (openRef.current) return;
    const trigger = triggerRef.current;
    const assistantOrb = document.querySelector(".ai-orb");
    const fallback = Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR))
      .find((candidate) => !candidate.closest(".evidence-sheet") && isAvailableFocusTarget(candidate));

    if (isAvailableFocusTarget(trigger)) focusWithoutScrolling(trigger);
    else if (isAvailableFocusTarget(assistantOrb)) focusWithoutScrolling(assistantOrb);
    else if (fallback instanceof HTMLElement) focusWithoutScrolling(fallback);
    else document.body.focus({ preventScroll: true });

    triggerRef.current = null;
    closeRequestedRef.current = false;
  }, []);

  const scheduleFocusRestoration = useCallback(() => {
    if (restoreFrameRef.current !== null) window.cancelAnimationFrame(restoreFrameRef.current);
    restoreFrameRef.current = window.requestAnimationFrame(() => {
      restoreFrameRef.current = null;
      restoreFocus();
    });
  }, [restoreFocus]);

  const requestClose = useCallback(() => {
    if (closeRequestedRef.current) return;
    closeRequestedRef.current = true;
    onClose();
  }, [onClose]);

  useEffect(() => {
    const trackExternalFocus = (event: FocusEvent) => {
      if (!openRef.current && event.target instanceof HTMLElement) {
        lastExternalFocusRef.current = event.target;
      }
    };
    document.addEventListener("focusin", trackExternalFocus, true);
    return () => document.removeEventListener("focusin", trackExternalFocus, true);
  }, []);

  useLayoutEffect(() => {
    const wasOpen = previouslyOpenRef.current;
    previouslyOpenRef.current = open;

    if (open && !wasOpen) {
      if (restoreFrameRef.current !== null) {
        window.cancelAnimationFrame(restoreFrameRef.current);
        restoreFrameRef.current = null;
      }
      const activeElement = document.activeElement;
      triggerRef.current = activeElement instanceof HTMLElement
        && activeElement !== document.body
        && !dialogRef.current?.contains(activeElement)
        ? activeElement
        : lastExternalFocusRef.current;
      closeRequestedRef.current = false;
      closeButtonRef.current?.focus({ preventScroll: true });
    } else if (!open && wasOpen) {
      scheduleFocusRestoration();
    }
  }, [open, scheduleFocusRestoration]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        requestClose();
        return;
      }
      if (event.key !== "Tab") return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter(isAvailableFocusTarget);
      if (focusable.length === 0) {
        event.preventDefault();
        focusWithoutScrolling(dialog);
        return;
      }

      const activeElement = document.activeElement;
      const currentIndex = focusable.indexOf(activeElement as HTMLElement);
      const shouldWrapBackward = event.shiftKey && currentIndex <= 0;
      const shouldWrapForward = !event.shiftKey && (currentIndex === -1 || currentIndex === focusable.length - 1);
      if (!shouldWrapBackward && !shouldWrapForward) return;

      event.preventDefault();
      focusWithoutScrolling(shouldWrapBackward ? focusable.at(-1)! : focusable[0]);
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, requestClose]);

  useEffect(() => () => {
    if (restoreFrameRef.current !== null) window.cancelAnimationFrame(restoreFrameRef.current);
  }, []);

  if (!paragraph) return null;

  return (
    <div className="evidence-backdrop" role="presentation" onMouseDown={requestClose}>
      <section ref={dialogRef} className="evidence-sheet" role="dialog" aria-modal="true" aria-label="课文证据页" tabIndex={-1} onMouseDown={(event) => event.stopPropagation()}>
        <div className="evidence-sheet__stamp"><CheckCircle2 size={22} /> 已核验</div>
        <button ref={closeButtonRef} className="icon-button evidence-sheet__close" type="button" onClick={requestClose} title="关闭证据页" aria-label="关闭证据页">
          <X size={20} />
        </button>
        <span className="section-kicker">{paragraph.id} · 原文</span>
        <h2>{title}</h2>
        <blockquote>{paragraph.text}</blockquote>
      </section>
    </div>
  );
}
